// TOP LEVEL IMPORTS
import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
// APOLLO
import { compose, graphql } from 'react-apollo';
import invoiceByIdQuery from 'ApolloClient/Queries/invoiceById';
import completeCreateInvoice from 'ApolloClient/Mutations/completeCreateInvoice';
// ANT
import Form from 'antd/lib/form';
import Icon from 'antd/lib/icon';
import message from 'antd/lib/message';
import notification from 'antd/lib/notification';
// LIB
import RecomposeHelpers from 'lib/helpers/RecomposeHelpers';
import InvoiceHelpers from 'lib/helpers/InvoiceHelpers';
import CurrencyHelpers from 'lib/helpers/CurrencyHelpers';
import ErrorHelpers from 'lib/helpers/ErrorHelpers';
// COMPONENTS
import InvoicePageHeader from './InvoicePageHeader';
import CreateStep from './CreateStep';
import ReviewStep from './ReviewStep';
import SendStep from './SendStep';
import FormButtons from './FormButtons';
import CompleteScreen from './CompleteScreen';

// STYLED COMPONENTS
// ========================================
const ContractStepContaier = styled.div`
  min-height: 95vh;
  background-color: #fbfcfc;
  flex-direction: column;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 40px;
  @media (max-width: 769px) {
    padding: 15px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 60px;
  margin-top: 15px;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const StepsContainer = styled.div`
  flex: 5;
  min-height: 500;
  width: 100%;
  padding: 5px;
  max-width: 100%;
`;

// HELPER COMPONENTS
// ================================
const BackButton = ({ onBack }) => {
  return (
    <div style={{ textAlign: 'center', marginTop: 25 }}>
      <a size="large" style={{ margiRight: 10, fontSize: 14 }} onClick={() => onBack()}>
        <Icon type="left" style={{ marginRight: 3 }} />
        GO BACK
      </a>
    </div>
  );
};

// CONSTANTS
// ================================
const DEFAULT_LINE_ITEM = {
  description: '',
  amount: 0,
};

const DEFAULT_SALES_TAX = 0;

const DEFAULT_INVOICE_STATE = {
  keys: [],
  clientId: null,
  clientEmailMessage: null,
  clientEmailSubject: null,
  clientEmail: null,
  memo: null,
  status: 'draft',
  salesTax: DEFAULT_SALES_TAX,
  lineItems: [],
  // DISCOUNT
  discountType: 'dollar-amount',
  currency: 'USD',
  discountValue: 0,
  discountAmount: 0,
  subtotalAfterDiscount: 0,
  //
  finalPaymentAmount: DEFAULT_LINE_ITEM.amount,
  totalWithTax: DEFAULT_LINE_ITEM.amount * (DEFAULT_SALES_TAX / 100),
  taxAmount: 0,
  total: DEFAULT_LINE_ITEM.amount,
  subtotalAfterTax: 0,
  subtotal: 0,
  finalPaymentDueDate: moment(),
  // SCHEDULED INVOICE
  scheduleInvoice: false,
  scheduledInvoiceDate: moment(),
};

class InvoiceCreate extends React.Component {
  state = {
    currentPageHeader: 0,
    creating: false,
    complete: false,
    sending: false,
    invoice: {
      ...DEFAULT_INVOICE_STATE,
      clientEmail: this.props.invoiceByIdQuery.invoiceById.client.email,
      salesTax:
        this.props.invoiceByIdQuery.invoiceById && this.props.invoiceByIdQuery.invoiceById.salesTax
          ? this.props.invoiceByIdQuery.invoiceById.salesTax
          : this.props.currentUser.organization && Number(this.props.currentUser.organization.salesTax),
    },
  };

  componentDidMount() {
    this.onInvoiceQueryComplete(this.props.invoiceByIdQuery.invoiceById);
  }

  getCleanedLineItems = keys => {
    return keys.map(key => {
      let itemValue = this.state.invoice[`names-${key}`];
      itemValue.amount = itemValue.amount.replace('.', '');
      return itemValue;
    });
  };

  getVariables = () => {
    const {
      clientEmail,
      clientEmailMessage,
      clientEmailSubject,
      memo,
      keys,
      finalPaymentAmount,
      totalWithTax,
      salesTax,
      total,
      taxAmount,
      discountType,
      discountValue,
      discountAmount,
      subtotalAfterDiscount,
      finalPaymentDueDate,
    } = this.state.invoice;

    let lineItems = this.getCleanedLineItems(keys);

    return {
      _id: this.props.match.params.invoiceId,
      params: {
        clientEmail,
        clientEmailMessage,
        clientEmailSubject,
        memo,
        finalPaymentAmount,
        amount: finalPaymentAmount,
        totalWithTax,
        salesTax,
        total,
        due: finalPaymentDueDate,
        taxAmount,
        lineItems,
        discountType,
        discountValue,
        discountAmount,
        subtotalAfterDiscount,
        scheduleInvoice: this.state.invoice.scheduleInvoice,
        scheduledInvoiceDate: this.state.invoice.scheduledInvoiceDate,
      },
    };
  };

  onComplete = async () => {
    let variables = this.getVariables();

    this.setState({ sending: true });

    try {
      await this.props.completeCreateInvoice({ variables });
    } catch (err) {
      this.setState({ sending: false });
      return notification.warning({
        description: err.message,
      });
    }

    this.setState({ complete: true, sending: false });
  };

  reCalculateAmounts = newState => {
    let { keys, salesTax, discountValue, discountAmount, discountType } = newState;
    let subtotal = InvoiceHelpers.getSubTotal(keys, newState);
    let subtotalAfterDiscount;
    let taxAmount;
    let subtotalAfterTax;
    let finalPaymentAmount;
    let lineItems;

    try {
      discountAmount = InvoiceHelpers.getDiscountAmount(discountValue, discountType, discountAmount, subtotal);
      subtotalAfterDiscount = subtotal - discountAmount;
      taxAmount = InvoiceHelpers.getTax(salesTax, subtotalAfterDiscount);
      subtotalAfterTax = subtotalAfterDiscount + taxAmount;
      finalPaymentAmount = subtotalAfterTax;
      lineItems = InvoiceHelpers.getLineItems(newState);
    } catch (err) {
      return message.error(err.message);
    }

    let newCalculations = {
      ...newState,
      subtotal: Math.round(subtotal),
      subtotalAfterTax: Math.round(subtotalAfterTax),
      discountAmount: Math.round(discountAmount),
      subtotalAfterDiscount: Math.round(subtotalAfterDiscount),
      finalPaymentAmount: Math.round(finalPaymentAmount),
      amount: Math.round(finalPaymentAmount),
      taxAmount: Math.round(taxAmount),
      lineItems,
    };

    return newCalculations;
  };

  getSalesTaxPercent = () => {
    let tax = 0;
    if (this.state.invoice.salesTax) {
      tax = this.state.invoice.salesTax;
    } else if (this.props.invoiceByIdQuery.invoiceById && this.props.invoiceByIdQuery.invoiceById.salesTax) {
      tax = this.props.invoiceByIdQuery.invoiceById.salesTax;
    } else if (this.props.currentUser.organization && this.props.currentUser.organization.salesTax) {
      tax = Number(this.props.currentUser.organization.salesTax);
    }
    return tax;
  };

  onInvoiceQueryComplete = invoiceQueryResult => {
    // recalculate everything based on the new invoice value
    let newCalculation = this.reCalculateAmounts({
      ...this.state.invoice,
      ...invoiceQueryResult,
    });
    // merge local state in with the latest database results
    this.setState({
      invoice: {
        ...this.state.invoice, // local default state
        ...invoiceQueryResult, // remote data to merge locally
        ...newCalculation,
        salesTax: this.getSalesTaxPercent(),
      },
    });
  };

  onChange = newInvoiceValue => {
    // recalculate everything based on the new invoice value
    let newCalculation = this.reCalculateAmounts({
      ...this.state.invoice,
      ...newInvoiceValue,
    });

    // update the invoice object with new value
    this.setState({
      invoice: {
        ...this.state.invoice,
        ...newInvoiceValue,
        ...newCalculation,
      },
    });
    // fire off mutation as an "auto-save"?
  };

  renderStep = () => {
    const { invoiceByIdQuery, currentUser } = this.props;

    let currencySymbol = CurrencyHelpers.getCurrencySymbol(
      (this.state.invoice && this.state.invoice.currency) || 'USD',
    );

    if (this.state.currentPageHeader === 0) {
      return (
        <CreateStep
          currentUser={currentUser}
          onChange={this.onChange}
          keys={this.state.invoice.keys}
          invoice={this.state.invoice}
          currencySymbol={currencySymbol}
        />
      );
    }

    if (this.state.currentPageHeader === 1) {
      return (
        <ReviewStep
          onBack={this.onBack}
          client={invoiceByIdQuery.invoiceById.client}
          invoice={this.state.invoice}
          currencySymbol={currencySymbol}
        />
      );
    }

    if (this.state.currentPageHeader === 2) {
      return (
        <SendStep
          currentUser={currentUser}
          onChange={this.onChange}
          onSubmit={this.onSubmit}
          invoice={this.state.invoice}
          currencySymbol={currencySymbol}
        />
      );
      // return (
      //   <SendStep {...this.props} invoiceFlow={invoiceFlow} form={this.props.form} currencySymbol={currencySymbol} />
      // );
    }

    if (this.state.currentPageHeader === 3) {
      return null;
    }
  };

  onSaveInvoiceChanges = async () => {
    try {
      console.log('onSaveInvoiceChanges');
    } catch (err) {
      return ErrorHelpers.handleError(err);
    }
  };

  onNext = () => {
    // if we're on the third page, return null-- GO NO FURTHER YOUNG ONE
    if (this.state.currentPageHeader === 3) {
      return null;
    }

    // if we're on the first page (0) or the finalPaymentAmount is less tahn zero, show warning
    if (this.state.currentPageHeader === 0 && this.state.invoice.finalPaymentAmount <= 0) {
      return notification.warning({
        description: `your invoice's total must be a positive number!`,
        message: `You can't do that!`,
      });
    }
    this.onSaveInvoiceChanges();
    // if you got this far, then we increment the current page, which is called currentPageHeader
    this.setState({ currentPageHeader: this.state.currentPageHeader + 1 });
  };

  onBack = () => {
    this.setState({ currentPageHeader: this.state.currentPageHeader - 1 });
  };

  renderBackButton = () => {
    if (this.state.currentPageHeader > 0) {
      return <BackButton onBack={this.onBack} />;
    }
  };

  render() {
    console.log(this.props);

    if (this.state.creating) {
      return null;
    }

    if (this.state.complete) {
      return (
        <CompleteScreen
          currentPageHeader={this.state.currentPageHeader}
          invoiceById={this.props.invoiceByIdQuery.invoiceById}
          history={this.props.history}
        />
      );
    }

    return (
      <Container>
        <InvoicePageHeader
          currentUser={this.props.currentUser}
          windowWidth={this.props.windowWidth}
          current={this.state.currentPageHeader}
        />
        <ContractStepContaier>
          <StepsContainer>{this.renderStep()}</StepsContainer>
          <Content>
            <FormButtons
              sending={this.state.sending}
              onComplete={this.onComplete}
              renderBackButton={this.renderBackButton}
              onNext={this.onNext}
              currentPageHeader={this.state.currentPageHeader}
            />
          </Content>
          <div style={{ flex: 1 }} />
        </ContractStepContaier>
      </Container>
    );
  }
}

let options = ({ match }) => {
  return {
    variables: { _id: match.params.invoiceId },
  };
};

export default compose(
  graphql(invoiceByIdQuery, { options, name: 'invoiceByIdQuery' }),
  RecomposeHelpers.errorHOC({ propName: 'invoiceByIdQuery' }),
  RecomposeHelpers.loadingHOC({ propName: 'invoiceByIdQuery' }),
  graphql(completeCreateInvoice, { options, name: 'completeCreateInvoice' }),
)(Form.create()(InvoiceCreate));



// WEBPACK FOOTER //
// ./src/routes/invoice-create/index.js